<template>
  <v-container fluid class="page">
    <!-- Page Hero Banner -->
    <div class="page-hero mb-6">
      <div>
        <h2 class="d-flex align-center">
          <v-icon color="primary" class="mr-2" large>mdi-file-percent-outline</v-icon>
          Impuestos y Padrones de IIBB
        </h2>
        <p>Subí y administrá las alícuotas de percepción mensuales de ARBA (Provincia) y AGIP (Capital) para automatizar la facturación.</p>
      </div>
      <div>
        <v-btn color="primary" outlined :loading="loading" @click="fetchStatus">
          <v-icon left>mdi-refresh</v-icon> Actualizar Estado
        </v-btn>
      </div>
    </div>

    <!-- Alert Messaging -->
    <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>
    <v-alert v-if="success" type="success" dense outlined class="mb-4">{{ success }}</v-alert>

    <v-row>
      <!-- ARBA Card -->
      <v-col cols="12" md="6">
        <v-card outlined class="rounded-xl overflow-hidden elevation-2">
          <v-card-title class="white--text d-flex justify-space-between align-center px-6 py-4" style="background: linear-gradient(135deg, #1e3a8a, #3b82f6);">
            <div class="d-flex align-center">
              <v-icon dark left class="mr-2">mdi-office-building-cog</v-icon>
              <span class="font-weight-bold">ARBA - Provincia de Bs. As.</span>
            </div>
            <v-chip dark :color="status.arba.loaded ? 'success' : 'amber'" small class="font-weight-bold">
              {{ status.arba.loaded ? 'ACTIVO' : 'PENDIENTE' }}
            </v-chip>
          </v-card-title>

          <v-card-text class="pa-6">
            <div class="d-flex align-center mb-6">
              <v-icon size="48" color="blue darken-2" class="mr-4">mdi-file-document-outline</v-icon>
              <div>
                <div class="text-subtitle-2 grey--text">Archivo Padrón Actual</div>
                <div class="text-h6 font-weight-bold" v-if="status.arba.loaded">arba_percepciones.txt</div>
                <div class="text-h6 font-weight-bold grey--text" v-else>No cargado este mes</div>
              </div>
            </div>

            <v-divider class="mb-4"></v-divider>

            <v-row dense class="mb-6">
              <v-col cols="6">
                <span class="text-caption grey--text">Tamaño del archivo</span>
                <div class="text-subtitle-1 font-weight-bold">{{ status.arba.sizeMb ? status.arba.sizeMb + ' MB' : '0.00 MB' }}</div>
              </v-col>
              <v-col cols="6">
                <span class="text-caption grey--text">Última actualización</span>
                <div class="text-subtitle-1 font-weight-bold">{{ status.arba.lastUploaded ? formatDate(status.arba.lastUploaded) : 'Sin registros' }}</div>
              </v-col>
            </v-row>

            <!-- File Upload Input -->
            <v-file-input
              v-model="files.arba"
              label="Seleccionar padrón ARBA (.txt)"
              outlined
              dense
              accept=".txt"
              prepend-icon="mdi-upload-outline"
              :loading="uploading.arba"
              placeholder="Hacé clic para seleccionar"
            ></v-file-input>

            <v-btn
              color="primary"
              block
              depressed
              class="font-weight-bold rounded-lg mt-2"
              :loading="uploading.arba"
              :disabled="!files.arba"
              @click="handleUpload('ARBA')"
            >
              <v-icon left>mdi-cloud-upload</v-icon> Subir Padrón ARBA
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- AGIP Card -->
      <v-col cols="12" md="6">
        <v-card outlined class="rounded-xl overflow-hidden elevation-2">
          <v-card-title class="white--text d-flex justify-space-between align-center px-6 py-4" style="background: linear-gradient(135deg, #065f46, #10b981);">
            <div class="d-flex align-center">
              <v-icon dark left class="mr-2">mdi-city-variant-outline</v-icon>
              <span class="font-weight-bold">AGIP - CABA</span>
            </div>
            <v-chip dark :color="status.agip.loaded ? 'success' : 'amber'" small class="font-weight-bold">
              {{ status.agip.loaded ? 'ACTIVO' : 'PENDIENTE' }}
            </v-chip>
          </v-card-title>

          <v-card-text class="pa-6">
            <div class="d-flex align-center mb-6">
              <v-icon size="48" color="emerald darken-2" class="mr-4">mdi-file-document-outline</v-icon>
              <div>
                <div class="text-subtitle-2 grey--text">Archivo Padrón Actual</div>
                <div class="text-h6 font-weight-bold" v-if="status.agip.loaded">agip_percepciones.txt</div>
                <div class="text-h6 font-weight-bold grey--text" v-else>No cargado este mes</div>
              </div>
            </div>

            <v-divider class="mb-4"></v-divider>

            <v-row dense class="mb-6">
              <v-col cols="6">
                <span class="text-caption grey--text">Tamaño del archivo</span>
                <div class="text-subtitle-1 font-weight-bold">{{ status.agip.sizeMb ? status.agip.sizeMb + ' MB' : '0.00 MB' }}</div>
              </v-col>
              <v-col cols="6">
                <span class="text-caption grey--text">Última actualización</span>
                <div class="text-subtitle-1 font-weight-bold">{{ status.agip.lastUploaded ? formatDate(status.agip.lastUploaded) : 'Sin registros' }}</div>
              </v-col>
            </v-row>

            <!-- File Upload Input -->
            <v-file-input
              v-model="files.agip"
              label="Seleccionar padrón AGIP (.txt)"
              outlined
              dense
              accept=".txt"
              prepend-icon="mdi-upload-outline"
              :loading="uploading.agip"
              placeholder="Hacé clic para seleccionar"
            ></v-file-input>

            <v-btn
              color="success"
              block
              depressed
              class="font-weight-bold rounded-lg mt-2"
              :loading="uploading.agip"
              :disabled="!files.agip"
              @click="handleUpload('AGIP')"
            >
              <v-icon left>mdi-cloud-upload</v-icon> Subir Padrón AGIP
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Cache Metrics & System Status -->
    <v-card outlined class="rounded-xl mt-6 pa-6 elevation-1">
      <v-row align="center">
        <v-col cols="12" md="8">
          <h3 class="d-flex align-center font-weight-bold mb-2">
            <v-icon color="grey darken-2" class="mr-2">mdi-database-clock-outline</v-icon>
            Métricas de Caché Fiscal de Clientes
          </h3>
          <p class="grey--text text-body-2 mb-0">
            Para maximizar el rendimiento, el sistema guarda las alícuotas del padrón en una base de datos local una vez consultadas. 
            El período activo actual es <strong class="primary--text">{{ status.cache.activePeriod }}</strong>.
          </p>
        </v-col>
        <v-col cols="12" md="4" class="text-md-right">
          <div class="d-inline-flex flex-column align-end">
            <span class="text-caption grey--text">Clientes en Caché este mes</span>
            <div class="text-h4 font-weight-black primary--text mb-2">{{ status.cache.cachedClientsCount }}</div>
          </div>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script>
import { iibbService } from "../services/iibb";

export default {
  name: "IibbTaxesConfig",
  data() {
    return {
      loading: false,
      error: null,
      success: null,
      status: {
        arba: { loaded: false, sizeMb: 0, lastUploaded: null },
        agip: { loaded: false, sizeMb: 0, lastUploaded: null },
        cache: { activePeriod: "", cachedClientsCount: 0 }
      },
      files: {
        arba: null,
        agip: null
      },
      uploading: {
        arba: false,
        agip: false
      }
    };
  },
  created() {
    this.fetchStatus();
  },
  methods: {
    async fetchStatus() {
      this.loading = true;
      this.error = null;
      try {
        const res = await iibbService.getStatus();
        if (res.ok) {
          this.status = res.data;
        } else {
          this.error = res.error || "Error al recuperar el estado impositivo";
        }
      } catch (err) {
        this.error = err.response?.data?.error || "Error de red al consultar estado";
      } finally {
        this.loading = false;
      }
    },
    async handleUpload(province) {
      const isArba = province === "ARBA";
      const file = isArba ? this.files.arba : this.files.agip;
      if (!file) return;

      if (isArba) this.uploading.arba = true;
      else this.uploading.agip = true;

      this.error = null;
      this.success = null;

      try {
        const res = await iibbService.uploadPadron(province, file);
        if (res.ok) {
          this.success = res.data.message || `Padrón de ${province} cargado con éxito.`;
          // Reset file input
          if (isArba) this.files.arba = null;
          else this.files.agip = null;
          // Refresh status
          await this.fetchStatus();
        } else {
          this.error = res.error || `Error al subir padrón de ${province}`;
        }
      } catch (err) {
        this.error = err.response?.data?.error || `Error de conexión al subir padrón de ${province}`;
      } finally {
        if (isArba) this.uploading.arba = false;
        else this.uploading.agip = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return d.toLocaleString("es-AR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
    }
  }
};
</script>

<style scoped>
.page-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 16px;
}
.page-hero h2 {
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.02em;
}
.page-hero p {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 14px;
}
</style>
